import { SK1, PERIODS } from 'constants/constants';
import * as yup from 'yup';
import { __, value2LocaleValue } from 'utils/helpers';

/**
 * The limits of the loan settings. The form's `max={100}` props were never applied and do not fit the values, which
 * are fractions (-0.00277 is -0.277 %); the limits are: interest rates (EURIBOR, fixed and SK1 rates) -1 to 1,
 * the other ratios and factors (Kreditnebenkosten, SK1 factor, own funds, rate ratio, interest bandwidth) 0 to 1,
 * the fixed loan costs (EUR, optional) 0 to 5000 (the form's max={5000}).
 */
const RATE = { min: -1, max: 1 };
const RATIO = { min: 0, max: 1 };
const LOAN_COSTS_FIX = { min: 0, max: 5000 };

const minMessage = (min: number) => () => __('errors.min_value', { min: value2LocaleValue(min) });
const maxMessage = (max: number) => () => __('errors.max_value', { max: value2LocaleValue(max) });

// yup 0.28 rejected null with the type error (yup 1: with the required message), hence nonNullable.
const requiredNumber = (id: string, { min, max }: { min: number; max: number }) =>
  yup
    .number()
    .required(() => __(id))
    .typeError(() => __(id))
    .nonNullable(() => __(id))
    .min(min, minMessage(min))
    .max(max, maxMessage(max));

/** An empty input is NaN (parseFloat of ''); the optional field saves it as null. */
const emptyAsNull = (value: unknown) => (Number.isNaN(value) ? null : value);

type TNumberSchema = ReturnType<typeof requiredNumber>;

const noRates: Record<string, TNumberSchema> = {};

export default yup.object().shape({
  euribor_6m: requiredNumber('errors.fill_field', RATE),
  fixedInterestRate_10: requiredNumber('errors.fill_field', RATE),
  fixedInterestRate_15: requiredNumber('errors.fill_field', RATE),
  fixedInterestRate_20: requiredNumber('errors.fill_field', RATE),
  // The untyped code started this reduce from [] (spread as nothing); {} is the same.
  ...SK1.reduce((result, sk) => {
    const interestRates = PERIODS.reduce((res, period) => {
      return { ...res, [`interestRate_${sk}_${period}`]: requiredNumber('errors.required', RATE) };
    }, noRates);
    return { ...result, ...interestRates };
  }, noRates),
  loanCosts: requiredNumber('errors.fill_field', RATIO),
  loanCostsFix: yup
    .number()
    .transform(emptyAsNull)
    .nullable()
    .min(LOAN_COSTS_FIX.min, minMessage(LOAN_COSTS_FIX.min))
    .max(LOAN_COSTS_FIX.max, maxMessage(LOAN_COSTS_FIX.max)),
  sk1Factor: requiredNumber('errors.fill_field', RATIO),
  ownFundsRatio: requiredNumber('errors.fill_field', RATIO),
  maxLoanRateRatio: requiredNumber('errors.fill_field', RATIO),
  interestBandwith: requiredNumber('errors.fill_field', RATIO),
});
